// Copyright (c) Docugami, Inc. All rights reserved.

import { IOverflowSetItemProps } from "@fluentui/react";
import { createFolderId, downloadFileFromUrl, getFileUxName } from "components/file_management/file_helpers";
import { isSingleDocumentReportView } from "components/reports/ReportsHelpers";
import { appContentColor } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { getIconInfoForWorkPath, isAbstractViewEnabled } from "components/work_path/WorkPathsHelper";
import { Config } from "config";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { DownloadFilesDgmlRequest } from "networking/api";
import { ReportViewContext } from "networking/reportsApi";
import { WorkPathType } from "networking/WorkPathApi";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { changeClusterForDocuments } from "store/clusters/actions";
import { FeatureFlags } from "store/featureFlags/types";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import { createOperationId, useConfirmDocumentsMutation, useDownloadDetailsMutation, useLazyGetAbstractTemplateExistsQuery, useLazyGetFlaggedDocumentsQuery, useLazyGetPublishedAbstractExistsQuery, useLazyGetPublishedSingleDocumentExcelExistsQuery, useLazyGetPublishedExcelExistsQuery, useUnpublishReportMutation } from "store/reports/reportsSlice";
import { createSelectDocumentsNeedingRepublishing, selectIsReportPublished, selectOrderedReportEntryIds, selectReportContext, selectReportDocumentId, selectReportDocumentsView, selectReportId, selectReportLabels, selectReportName, selectReportViewMode } from "store/reports/selectors";
import { DownloadSpreadsheetPreviewSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { getAutomationIdForWorkPathConnector } from "utils/Automation";
import { dgIconNames } from "./dgIcons";
import { useDownloadFilesCommand } from "./DownloadFilesCommand";
import { useFeatureFlag } from "./FeatureFlags";
import { ModalCommandItem } from "./ModalCommandItem";
import { useWorkPathBrandName } from "./useWorkPathTypeName";

export interface IWorkPathCommandItem extends IOverflowSetItemProps, ModalCommandItem {
    onOverflowOpened?: () => void;
    dataAutomationId?: string;
}

export interface IRepublishDocumentsCommandItem extends IWorkPathCommandItem {
    title?: string;
    confirmText?: string
}

const selectDocumentsNeedingRepublishing = createSelectDocumentsNeedingRepublishing();

export const useRepublishDocumentsCommand = (): IRepublishDocumentsCommandItem => {
    const [showConfirmation, setShowConfirmation] = useState(false);

    const reportId = useAppSelector(selectReportId);
    const viewMode = useAppSelector(selectReportViewMode);
    const folderId = useAppSelector(selectReportDocumentId);
    const context = useAppSelector(selectReportContext);

    const [triggerConfirmDocuments] = useConfirmDocumentsMutation();

    const documentsNeedingRepublishing = useAppSelector(state => selectDocumentsNeedingRepublishing(state, reportId));

    const republishCandidates = useMemo(() => {
        if (context !== ReportViewContext.Published || !folderId) {
            return [];
        }

        if (isSingleDocumentReportView(viewMode)) {
            return !!documentsNeedingRepublishing?.[folderId] ? [folderId] : []
        }

        return Object.keys(documentsNeedingRepublishing ?? {});
    }, [context, folderId, viewMode, documentsNeedingRepublishing])

    const onConfirm = () => {
        triggerConfirmDocuments({
            reportId: reportId,
            folderIds: republishCandidates,
            operationId: createOperationId()
        });

        setShowConfirmation(false);
    }

    const documentPlurality = !isSingleDocumentReportView(viewMode) ? '(s)' : ''
    return {
        key: 'republishDocuments',
        name: `Republish Document${documentPlurality}`,
        onClick: () => setShowConfirmation(true),
        onDismiss: () => setShowConfirmation(false),
        isModalVisible: showConfirmation,
        isVisible: !!republishCandidates.length,
        trigger: onConfirm,
        title: `Continuing will republish your document${documentPlurality} so that any changes made can be safely saved.`,
        confirmText: `REPUBLISH DOCUMENT${documentPlurality}`,
        iconDescriptor: {
            name: 'Rerun',
            height: .7,
            width: .7,
            defaultColor: appContentColor
        }
    }
}

export const useExcelDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const isPublished = useAppSelector(selectIsReportPublished);

    const workPathName = useAppSelector(state => state.workPaths.currentWorkPath?.name);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download excel file");
    const [triggerFetchExists, excelExistsResult] = useLazyGetPublishedExcelExistsQuery();

    return {
        key: 'excel',
        name: downloadStatus === RequestState.InProgress ? 'Downloading Spreadsheet...' : 'Download Spreadsheet',
        onClick: () => executeDownload(downloadFileFromUrl(
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/excel`,
            `${workPathName!}.xlsx`,
            'GET'
        )),
        isVisible: isPublished,
        disabled: downloadStatus === RequestState.InProgress || !excelExistsResult.data,
        onOverflowOpened: () => triggerFetchExists({ reportId: reportId }),
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useExcelPreviewDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const context = useAppSelector(selectReportContext);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);
    const folderIds = useAppSelector(selectReportDocumentsView)?.ids;

    const workPathName = useAppSelector(state => state.workPaths.currentWorkPath?.name);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download excel preview file");

    function onDownloadClick() {
        Telemetry.trackEvent<DownloadSpreadsheetPreviewSchema>({
            eventName: TelemetryEvent.DownloadSpreadsheetPreview
        });
        executeDownload(downloadFileFromUrl(
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/excel/on-demand`,
            `${workPathName!}_preview.xlsx`,
            'PUT',
            folderIds
        ));
    }

    return {
        key: 'excelpreview',
        name: downloadStatus === RequestState.InProgress ? 'Downloading Spreadsheet Preview...' : 'Download Spreadsheet Preview',
        onClick: () => onDownloadClick(),
        isVisible: context !== ReportViewContext.Published && !!entryIds?.length,
        disabled: downloadStatus === RequestState.InProgress,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useDownloadReportDetailsDebugCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const name = useAppSelector(selectReportName);

    const [trigger, status] = useDownloadDetailsMutation();
    const enabled = useFeatureFlag(FeatureFlags.DiagnosticLoggingEnabled);

    async function onClick() {
        const response = await trigger(reportId).unwrap();

        if (!!response && !!name) {
            const a = document.createElement('a');
            a.href = response;
            a.download = `${name}-details.json`;
            a.click();
            URL.revokeObjectURL(response);
        }
    }

    return {
        key: 'report-details',
        name: status.isLoading ? 'Downloading Details...' : 'Download Details',
        onClick: onClick,
        isVisible: enabled,
        disabled: status.isLoading,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useAbstractTemplateUploadCommand = (): IWorkPathCommandItem => {
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const isPublished = useAppSelector(selectIsReportPublished);
    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);

    return {
        key: 'upload',
        name: `Upload Abstract Template`,
        onClick: () => setIsUploadModalOpen(true),
        onDismiss: () => setIsUploadModalOpen(false),
        isModalVisible: isUploadModalOpen,
        disabled: isUploadModalOpen,
        isVisible: isPublished && workPathType === WorkPathType.Abstract,
        iconDescriptor: {
            name: dgIconNames.DgExportIcon,
            height: 1,
            width: 1.39,
            defaultColor: appContentColor
        }
    }
}

export const useAbstractTemplateDefaultDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const isPublished = useAppSelector(selectIsReportPublished);

    const workPathName = useAppSelector(state => state.workPaths.currentWorkPath?.name);
    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download template");

    return {
        key: 'template',
        name: 'Download Default Abstract Template',
        onClick: () => executeDownload(downloadFileFromUrl(
            // Using the downloading of the default for the time being.
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/abstractTemplate/default`,
            `${workPathName!}.docx`,
            'GET'
        )),
        isVisible: isPublished && workPathType === WorkPathType.Abstract,
        disabled: downloadStatus === RequestState.InProgress,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useAbstractTemplateDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const isPublished = useAppSelector(selectIsReportPublished);
    const workPathName = useAppSelector(state => state.workPaths.currentWorkPath?.name);
    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download template");
    const [triggerFetchExists, abstractTemplateExistsResult] = useLazyGetAbstractTemplateExistsQuery()

    return {
        key: 'template',
        name: 'Download Abstract Template',
        onClick: () => executeDownload(downloadFileFromUrl(
            // Using the downloading of the default for the time being.
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/abstractTemplate`,
            `${workPathName!}.docx`,
            'GET'
        )),
        isVisible: isPublished && workPathType === WorkPathType.Abstract,
        disabled: downloadStatus === RequestState.InProgress && !abstractTemplateExistsResult.data,
        onOverflowOpened: () => triggerFetchExists({ reportId: reportId }),
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useDgmlDownloadCommand = (folderId: string): IWorkPathCommandItem => {
    const hasDgml = useAppSelector(state => !!state.documents.indexed[folderId]?.clusterDocument?.hasCpml)
    const currentFileName = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument?.filename);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download dgml file");

    const download = useCallback(() => executeDownload(downloadFileFromUrl(
        `${Config.fileManagerApiUrl}/files/semanticdgml`,
        `${getFileUxName(currentFileName, true)!}.xml`,
        'POST', {
            files: [{
                folderId: folderId,
                fileName: `${getFileUxName(currentFileName, true)!}.xml`
            }]
        } as DownloadFilesDgmlRequest
    )), [folderId, currentFileName, executeDownload]);

    return {
        key: 'dgml',
        name: 'Download DGML',
        trigger: () => download(),
        onClick: () => download(),
        isVisible: !!folderId,
        disabled: !hasDgml || downloadStatus === RequestState.InProgress,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useRepublishDocumentCommand = (folderId: string, documentsNeedsRepublishing: boolean): IWorkPathCommandItem => {
    const [triggerConfirmDocuments, confirmDocumentsResult] = useConfirmDocumentsMutation();
    const reportId = useAppSelector(selectReportId);
    const hasDgml = useAppSelector(state => !!state.documents.indexed[folderId]?.clusterDocument.hasCpml);

    const republish = () => {
        triggerConfirmDocuments({
            reportId: reportId,
            folderIds: [folderId],
            operationId: createOperationId(),
        });
    }

    return {
        key: 'republishDocument',
        name: 'Republish Document',
        trigger: republish,
        onClick: republish,
        isVisible: documentsNeedsRepublishing,
        disabled: !documentsNeedsRepublishing || confirmDocumentsResult.isLoading || !hasDgml,
    }
}

export const useDownloadDocumentCommand = (folderId: string): IWorkPathCommandItem => {
    const filePath = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument.fileId);
    const filePaths = useMemo(() => !!filePath ? [filePath] : [], [filePath]);

    const { isActive, isEnabled, execute } = useDownloadFilesCommand(filePaths);

    return {
        key: 'download_document',
        name: 'Download Document',
        trigger: execute,
        onClick: execute,
        isVisible: !!filePaths.length,
        disabled: isActive || !isEnabled,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useRemoveDocumentCommand = (folderId: string): { command: IWorkPathCommandItem, overlay: ReactNode } => {
    const [showWarning, setShowWarning] = useState(false);
    const docSet = useAppSelector(state => state.clusters.indexed[!!folderId ? createFolderId(folderId).clusterId : '']?.clusterInfo);
    const docSetDocThreshold = useAppSelector(selectDocSetMinFiles) ?? 0;
    const filePath = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument.fileId);
    const filePaths = useMemo(() => !!filePath ? [filePath] : [], [filePath]);

    const { execute, state } = useAsyncHelper('Remove documents from doc set');
    const dispatch = useAppDispatch();

    const isEnabled = !docSet?.isDocugamiCluster;
    const unassignDocuments = () => execute(dispatch(changeClusterForDocuments('', filePaths)));

    function onRemoveDocuments() {
        if (docSet?.numberOfDocuments === docSetDocThreshold) {
            setShowWarning(true);
        } else {
            unassignDocuments();
        }
    }

    return {
        command: {
            key: 'remove_document',
            name: `Remove Document from Doc Set`,
            trigger: onRemoveDocuments,
            onClick: onRemoveDocuments,
            isVisible: !!filePaths.length && isEnabled,
            disabled: state === RequestState.InProgress,
            iconDescriptor: {
                name: dgIconNames.DgTrashCan,
                height: 1,
                width: .87,
                defaultColor: appContentColor
            }
        },
        overlay: (
            <ConfirmationDialog
                hidden={!showWarning}
                title={`Are you sure?`}
                subText={`Removing this document will make your doc set incomplete. You will not be able to create new projects with this doc set unless it has ${docSetDocThreshold} or more documents.`}
                confirmButtonText="Confirm"
                confirmedCallback={() => { unassignDocuments(); setShowWarning(false); }}
                dismissedCallback={() => setShowWarning(false)} />
        )
    }
}

export const useAbstractDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);
    const viewMode = useAppSelector(selectReportViewMode);
    const isPublished = useAppSelector(selectIsReportPublished);

    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);
    const currentFileName = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument?.filename);

    const abstractEnabled = isAbstractViewEnabled(workPathType);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download abstract file");
    const [triggerFetchExists, abstractExistsResult] = useLazyGetPublishedAbstractExistsQuery();

    return {
        key: 'abstract',
        name: downloadStatus === RequestState.InProgress ? 'Downloading Abstract...' : 'Download Abstract',
        onClick: () => executeDownload(downloadFileFromUrl(
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/${encodeURIComponent(folderId)}/abstract`,
            `${getFileUxName(currentFileName, true)}`,
            'GET',
            undefined,
            true
        )),
        isVisible: isPublished && abstractEnabled && isSingleDocumentReportView(viewMode),
        disabled: downloadStatus === RequestState.InProgress || !folderId || !abstractExistsResult.data || !currentFileName,
        onOverflowOpened: () => {
            if (abstractEnabled) {
                triggerFetchExists({ reportId: reportId, folderId: folderId })
            }
        },
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const usePreviewAbstractDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);
    const viewMode = useAppSelector(selectReportViewMode);
    const isPublished = useAppSelector(selectIsReportPublished);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);

    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);
    const currentFileName = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument?.filename);

    const abstractEnabled = isAbstractViewEnabled(workPathType);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download preview abstract file");

    return {
        key: 'abstractpreview',
        name: downloadStatus === RequestState.InProgress ? 'Downloading Abstract Preview...' : 'Download Abstract Preview',
        onClick: () => executeDownload(downloadFileFromUrl(
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/${encodeURIComponent(folderId)}/abstract/on-demand`,
            `${getFileUxName(currentFileName, true)}`,
            'GET',
            undefined,
            true
        )),
        isVisible: !isPublished && !!entryIds?.length && abstractEnabled && isSingleDocumentReportView(viewMode),
        disabled: downloadStatus === RequestState.InProgress || !folderId || !currentFileName,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useSingleDocumentExcelDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);
    const viewMode = useAppSelector(selectReportViewMode);
    const isPublished = useAppSelector(selectIsReportPublished);

    const currentFileName = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument?.filename);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download single document Excel file");
    const [triggerFetchExists, abstractExistsResult] = useLazyGetPublishedSingleDocumentExcelExistsQuery();

    return {
        key: 'singledocumentexcel',
        name: downloadStatus === RequestState.InProgress ? 'Downloading Excel...' : 'Download Excel',
        onClick: () => executeDownload(downloadFileFromUrl(
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/${encodeURIComponent(folderId)}/excel/singledocument`,
            `${getFileUxName(currentFileName, true)}`,
            'GET',
            undefined,
            true
        )),
        isVisible: isPublished && isSingleDocumentReportView(viewMode),
        disabled: downloadStatus === RequestState.InProgress || !folderId || !abstractExistsResult.data || !currentFileName,
        onOverflowOpened: () => {
            triggerFetchExists({ reportId: reportId, folderId: folderId })
        },
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const usePreviewSingleDocumentExcelDownloadCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);
    const viewMode = useAppSelector(selectReportViewMode);
    const isPublished = useAppSelector(selectIsReportPublished);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);

    const currentFileName = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument?.filename);

    const { execute: executeDownload, state: downloadStatus } = useAsyncHelper("Download single document Excel preview file");

    return {
        key: 'singledocumentexcelpreview',
        name: downloadStatus === RequestState.InProgress ? 'Downloading Excel Preview...' : 'Download Excel Preview',
        onClick: () => executeDownload(downloadFileFromUrl(
            `${Config.reportsApiUrl}/stream/reports/${encodeURIComponent(reportId)}/resources/${encodeURIComponent(folderId)}/excel/singledocument/on-demand`,
            `${getFileUxName(currentFileName, true)}`,
            'GET',
            undefined,
            true
        )),
        isVisible: !isPublished &&!!entryIds?.length && isSingleDocumentReportView(viewMode),
        disabled: downloadStatus === RequestState.InProgress || !folderId || !currentFileName,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useEditReportCommand = (): IWorkPathCommandItem => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const reportId = useAppSelector(selectReportId);
    const reportLabels = useAppSelector(selectReportLabels);
    const isPublished = useAppSelector(selectIsReportPublished);

    const [triggerUnpublish, unpublishState] = useUnpublishReportMutation();

    useEffect(() => {
        if (!unpublishState.isLoading) {
            setIsEditModalOpen(false);
        }
    }, [unpublishState.isLoading]);

    return {
        key: 'edit',
        name: `Edit ${reportLabels.titleName}`,
        onClick: () => setIsEditModalOpen(true),
        onDismiss: () => setIsEditModalOpen(false),
        trigger: () => triggerUnpublish({ reportId: reportId, operationId: createOperationId() }),
        isVisible: isPublished,
        isModalVisible: isEditModalOpen,
        disabled: unpublishState.isLoading,
        iconDescriptor: {
            name: 'Edit',
            height: 1,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useWpDocsetModelStatsCommand = (): IWorkPathCommandItem => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return {
        key: 'stats',
        name: 'Accuracy History',
        onClick: () => setIsModalOpen(true),
        onDismiss: () => setIsModalOpen(false),
        isVisible: true,
        isModalVisible: isModalOpen,
        disabled: false,
        iconDescriptor: {
            name: 'LineChart',
            height: 1,
            width: 1,
            defaultColor: appContentColor
        }
    }
}

export const useStorageProviderExportCommand = (): IWorkPathCommandItem => {
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const reportLabels = useAppSelector(selectReportLabels);
    const isPublished = useAppSelector(selectIsReportPublished);

    return {
        key: 'export',
        name: `Export ${reportLabels.titleName}`,
        onClick: () => setIsExportModalOpen(true),
        onDismiss: () => setIsExportModalOpen(false),
        isModalVisible: isExportModalOpen,
        disabled: isExportModalOpen,
        isVisible: isPublished,
        iconDescriptor: {
            name: dgIconNames.DgExportIcon,
            height: 1,
            width: 1.39,
            defaultColor: appContentColor
        }
    }
}

export const useConnectorInfoCommand = (): IWorkPathCommandItem => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isPublished = useAppSelector(selectIsReportPublished);

    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);
    const brandName = useWorkPathBrandName(workPathType);
    const isSupported = workPathType === WorkPathType.AutomationAnywhereDocumentAssembly ||
        workPathType === WorkPathType.AutomationAnywhereWorkFlow ||
        workPathType === WorkPathType.UiPathDocumentAssembly ||
        workPathType === WorkPathType.UiPathWorkFlow ||
        workPathType === WorkPathType.ZapierWorkFlow ||
        workPathType === WorkPathType.PowerAutomateWorkFlow

    return {
        key: 'connect',
        name: `Connect to ${brandName}`,
        onClick: () => setIsModalOpen(true),
        onDismiss: () => setIsModalOpen(false),
        isModalVisible: isModalOpen,
        disabled: isModalOpen,
        isVisible: isSupported && isPublished,
        iconDescriptor: !!workPathType ? getIconInfoForWorkPath(workPathType, true) : undefined,
        dataAutomationId: getAutomationIdForWorkPathConnector(workPathType)
    }
}

export const useDownloadFlaggedDocumentsCommand = (): IWorkPathCommandItem => {
    const reportId = useAppSelector(selectReportId);
    const isPublished = useAppSelector(selectIsReportPublished);

    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);

    const [triggerFetchDocuments, { data }] = useLazyGetFlaggedDocumentsQuery();
    const flaggedDocumentPaths = useMemo(() => data?.documents.map(x => x.filePath) ?? [], [data])
    const downloadCommand = useDownloadFilesCommand(flaggedDocumentPaths);

    const isSupported = workPathType === WorkPathType.DiligenceReport;

    return {
        key: 'local_download_flagged',
        name: `Download Flagged Files`,
        onClick: downloadCommand.execute,
        onOverflowOpened: () => triggerFetchDocuments({ reportId: reportId }),
        disabled: !downloadCommand.isEnabled || downloadCommand.isActive,
        isVisible: isSupported && isPublished,
        isActive: downloadCommand.isActive,
        iconDescriptor: {
            name: dgIconNames.DgDownloadIcon,
            height: .89,
            width: 1,
            defaultColor: appContentColor
        }
    }
}
